import { z } from "zod/v4";
import { defineResource, defineEndpoint } from "@visioni/a3s-client-api";
import { FEATURE_NAME } from "../constants";
import { ItemByIdPath } from "./get-report";

/**
 * Generic per-site artifact presign for multi-file (binary) reports — the
 * single download/render surface chosen in the plan (Option A). One route
 * serves BOTH a site's render `.json` ({@link SitePayloadKey}) and its download
 * `.xlsx` ({@link SiteWorkbookKey}); the `key` already encodes the site, so no
 * `siteId` path param is needed.
 *
 * It is keyed by the regen-stable `reportId` (reusing {@link ItemByIdPath}, the
 * same key the canonical by-id fetch uses) because cumulative RoadSense reports
 * share a `periodStart` and so can't be addressed by the legacy `periodStart`
 * key. Two guards apply, mirroring the snapshot-url route: the router middleware
 * confirms the caller is a tenant admin, then the handler 403s (never 404s)
 * unless the key's `(type, tenant, periodStart, reportId)` prefix matches the
 * resolved row — so the response never leaks whether arbitrary bucket keys (or
 * other tenants' reports) exist.
 */

// `key` stays a plain string here: its structural shape is validated by the
// `SiteWorkbookKey` / `SitePayloadKey` codecs in the handler, so a Zod
// refinement would only re-run the same parser.
export const ArtifactUrlQuery = z.object({
  key: z.string().min(1),
});
export type ArtifactUrlQuery = z.output<typeof ArtifactUrlQuery>;

export const ArtifactUrlResponse = z.object({
  data: z.object({
    url: z.string(),
    expiresInSeconds: z.int().positive(),
  }),
});
export type ArtifactUrlResponse = z.output<typeof ArtifactUrlResponse>;

export const reportsArtifactUrlResource = defineResource({
  feature: FEATURE_NAME,
  resource: "{type}/items/by-id/{reportId}/artifact-url",
  resourcePathSchema: ItemByIdPath,
  scope: "tenant",
});

export const getReportArtifactUrlEndpoint = defineEndpoint(reportsArtifactUrlResource, {
  responseSchema: ArtifactUrlResponse,
  querySchema: ArtifactUrlQuery,
});
